import React, { useEffect, useState, useCallback } from "react";

// Helper: get doctor from localStorage
const getDoctorData = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
};

// Helper: get all appointments from localStorage
const getAppointments = () => {
  try {
    return JSON.parse(localStorage.getItem("appointments")) || [];
  } catch {
    return [];
  }
};

const CalendarInput = ({ selectedDate, setSelectedDate }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [highlightAmounts, setHighlightAmounts] = useState({});

  const doctor = getDoctorData();
  const doctorId = doctor?.id || doctor?.email || "";

  // Compute counts per date for the given range
  const computeCounts = useCallback(() => {
    const year = currentMonth.getFullYear();
    // We'll consider dates from Jan 1 of previous year to Dec 31 of next year
    const startDate = new Date(year - 1, 0, 1);
    const endDate = new Date(year + 1, 11, 31);

    const allAppointments = getAppointments();

    // Filter appointments for this doctor (by doctorId or email)
    const doctorAppointments = allAppointments.filter(
      (apt) => apt.doctorId === doctorId || apt.doctorEmail === doctorId
    );

    // Count per date (YYYY-MM-DD format)
    const counts = {};
    doctorAppointments.forEach((apt) => {
      const dateKey = apt.appointmentDate; // assuming it's in "YYYY-MM-DD" format
      if (dateKey) {
        counts[dateKey] = (counts[dateKey] || 0) + 1;
      }
    });

    setHighlightAmounts(counts);
  }, [currentMonth, doctorId]);

  // Recompute when month or doctor changes
  useEffect(() => {
    computeCounts();
  }, [computeCounts]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getDayOfWeek = (year, month, day) => {
    return new Date(year, month, day).getDay();
  };

  const getCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfWeek = getDayOfWeek(year, month, 1);
    const calendarDays = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      calendarDays.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(day);
    }
    return calendarDays;
  };

  const handleDateClick = (day) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const date = new Date(year, month, day);
    setSelectedDate(date);
    setShowCalendar(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const renderCalendar = () => {
    if (!showCalendar) return null;

    const calendarDays = getCalendarDays();

    return (
      <div
        id="login-popup"
        tabIndex="-1"
        className="overflow-x-hidden fixed top-1 right-0 left-0 z-50 h-full items-center justify-center flex"
      >
        <div className="relative w-full max-w-md h-full md:h-auto">
          <div className="absolute mt-2 bg-white border border-gray-300 rounded-md shadow-lg p-4 w-full mx-2">
            <div className="flex justify-between">
              <button
                onClick={handlePrevMonth}
                className="text-gray-600 hover:text-gray-800"
              >
                Prev
              </button>
              <h2 className="font-bold text-lg">
                {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h2>
              <button
                onClick={handleNextMonth}
                className="text-gray-600 hover:text-gray-800"
              >
                Next
              </button>
            </div>
            <div className="grid grid-cols-7 text-center mt-2">
              {daysOfWeek.map((day, index) => (
                <div key={index} className="text-lg font-semibold text-gray-700">
                  {day}
                </div>
              ))}
              {calendarDays.map((day, index) => {
                // Build date key for this day
                const year = currentMonth.getFullYear();
                const month = currentMonth.getMonth();
                const dateKey =
                  day !== null
                    ? `${year}-${String(month + 1).padStart(2, "0")}-${String(
                        day
                      ).padStart(2, "0")}`
                    : null;

                const count = dateKey ? highlightAmounts[dateKey] : 0;

                return (
                  <div key={index} className="grid grid-rows-2">
                    <div
                      className={`p-1 cursor-pointer text-sm ${
                        selectedDate &&
                        day === selectedDate.getDate() &&
                        month === selectedDate.getMonth() &&
                        year === selectedDate.getFullYear()
                          ? "bg-blue-500 rounded-full text-white"
                          : ""
                      }`}
                      onClick={() => handleDateClick(day)}
                    >
                      {day}
                    </div>
                    <div className="text-xs text-teal-500">
                      {count > 0 ? count : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative mx-auto max-w-md w-full">
      <div className="col-span-2 sm:col-span-1">
        <label
          htmlFor="card-number"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Schedule Date <span className="text-red-600">*</span>
        </label>
        <input
          className="w-full py-2 px-4 border border-gray-400 rounded-lg focus:outline-none focus:border-blue-500"
          id="service"
          name="appointmentDate"
          placeholder="Choose a date..."
          onClick={() => setShowCalendar(true)}
          value={selectedDate ? selectedDate.toLocaleDateString() : ""}
          readOnly
        />
      </div>
      {renderCalendar()}
    </div>
  );
};

export default CalendarInput;